import React from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Chip, Stack, Paper, Alert,
} from '@mui/material';
import {
  Warning, Phone, CheckCircle, DirectionsCar, LocationOn, ArrowForward,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { emergencyNumbers, hospitalInfo } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

const EmergencyPage: React.FC = () => {
  const { t, language } = useLanguage();

  const emergencyTypes = [
    { icon: '🫀', en: 'Cardiac Arrest / Heart Attack', fr: 'Arrêt Cardiaque / Crise Cardiaque' },
    { icon: '🧠', en: 'Stroke', fr: 'AVC' },
    { icon: '🩸', en: 'Severe Bleeding', fr: 'Saignement Grave' },
    { icon: '🦴', en: 'Fractures & Trauma', fr: 'Fractures & Traumatismes' },
    { icon: '🫁', en: 'Breathing Difficulties', fr: 'Difficultés Respiratoires' },
    { icon: '🤰', en: 'Obstetric Emergencies', fr: 'Urgences Obstétricales' },
    { icon: '👶', en: 'Pediatric Emergencies', fr: 'Urgences Pédiatriques' },
    { icon: '🔥', en: 'Burns & Poisoning', fr: 'Brûlures & Empoisonnement' },
    { icon: '🚗', en: 'Road Accident Injuries', fr: 'Blessures d\'Accidents de Route' },
    { icon: '🦟', en: 'Severe Malaria / Infections', fr: 'Paludisme Grave / Infections' },
    { icon: '💉', en: 'Diabetic Emergencies', fr: 'Urgences Diabétiques' },
    { icon: '⚡', en: 'Seizures / Convulsions', fr: 'Crises / Convulsions' },
  ];

  const steps = [
    { num: 1, text: t.emergency.step1 },
    { num: 2, text: t.emergency.step2 },
    { num: 3, text: t.emergency.step3 },
    { num: 4, text: t.emergency.step4 },
  ];

  const emergSubCards = [
    { icon: '🚨', titleEn: 'Trauma & Injury Care', titleFr: 'Traumatismes & Blessures', bgColor: '#B71C1C' },
    { icon: '🫀', titleEn: 'Cardiac Emergencies', titleFr: 'Urgences Cardiaques', bgColor: '#C62828' },
    { icon: '⏱️', titleEn: 'Triage & Rapid Assessment', titleFr: 'Triage & Évaluation Rapide', bgColor: '#D32F2F' },
    { icon: '🚑', titleEn: 'Ambulance Services', titleFr: 'Services Ambulanciers', bgColor: '#E53935' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <PageHero
        title={t.emergency.title}
        subtitle={t.emergency.subtitle}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: language === 'en' ? 'Emergency' : 'Urgences' },
        ]}
        accentColor="#C62828"
      />

      {/* Sub-category cards */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {emergSubCards.map((card, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
                <Box sx={{ height: 140, background: `linear-gradient(135deg, ${card.bgColor}CC 0%, ${card.bgColor}88 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
                  {card.icon}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ borderLeft: '3px solid', borderColor: 'error.main', pl: 1.5, mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                      {language === 'en' ? card.titleEn : card.titleFr}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'error.main', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {language === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowForward sx={{ fontSize: 14 }} />
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Grid container spacing={4}>

          {/* ── Left column: contacts + alert ── */}
          <Grid item xs={12} md={4}>
            <Typography variant="h5" fontWeight={700} mb={3}>
              {language === 'en' ? 'Emergency Contacts' : 'Contacts d\'Urgence'}
            </Typography>
            <Stack spacing={2.5}>

              {/* Main number */}
              <Card sx={{ border: '2px solid', borderColor: 'error.main', borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Phone sx={{ color: 'error.main' }} />
                    <Typography variant="subtitle1" fontWeight={700} color="error.main">
                      {t.emergency.mainNumber}
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight={800} color="error.main" mb={1}>
                    {emergencyNumbers.main}
                  </Typography>
                  <Chip label="24h/24 — 7j/7" size="small" sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 700 }} />
                </CardContent>
              </Card>

              {/* Ambulance */}
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <DirectionsCar sx={{ color: 'warning.main' }} />
                    <Typography variant="subtitle1" fontWeight={700}>{t.emergency.ambulance}</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={800} mb={1}>{emergencyNumbers.ambulance}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'en' ? 'Rapid intervention anywhere in Bafoussam' : 'Intervention rapide partout à Bafoussam'}
                  </Typography>
                </CardContent>
              </Card>

              {/* Location */}
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocationOn sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight={700}>{t.emergency.location}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    {language === 'en' ? hospitalInfo.addressEn : hospitalInfo.addressFr}
                  </Typography>
                </CardContent>
              </Card>

              {/* Alert */}
              <Alert severity="error" icon={<Warning />} sx={{ borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>{t.emergency.urgentCare}</Typography>
                <Typography variant="body2">{t.emergency.urgentDesc}</Typography>
              </Alert>
            </Stack>
          </Grid>

          {/* ── Right column: what to do + emergency types ── */}
          <Grid item xs={12} md={8}>
            <Typography variant="h5" fontWeight={700} mb={3}>{t.emergency.whatToDo}</Typography>
            <Stack spacing={2} mb={5}>
              {steps.map((step) => (
                <Paper key={step.num} elevation={0}
                  sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.1rem', flexShrink: 0 }}>
                    {step.num}
                  </Box>
                  <Typography variant="body1" lineHeight={1.7} pt={0.5}>{step.text}</Typography>
                </Paper>
              ))}
            </Stack>

            <Typography variant="h5" fontWeight={700} mb={3}>{t.emergency.emergencyTypes}</Typography>
            <Grid container spacing={1.5} sx={{ mb: 4 }}>
              {emergencyTypes.map((type, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper elevation={0}
                    sx={{ p: 1.75, border: '1px solid', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                    <Typography fontSize="1.4rem">{type.icon}</Typography>
                    <Typography variant="body2" fontWeight={600} lineHeight={1.4}>
                      {language === 'en' ? type.en : type.fr}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Do's and Don'ts */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card sx={{ borderTop: '3px solid', borderColor: 'success.main', borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700} color="success.main" mb={2}>
                      ✅ {language === 'en' ? "Do's" : 'À Faire'}
                    </Typography>
                    {[
                      language === 'en' ? 'Stay calm and act quickly' : 'Restez calme et agissez rapidement',
                      language === 'en' ? 'Call emergency services immediately' : 'Appelez les urgences immédiatement',
                      language === 'en' ? 'Keep the patient awake and conscious' : 'Maintenez le patient éveillé et conscient',
                      language === 'en' ? 'Give first aid if trained' : 'Donnez les premiers secours si vous êtes formé',
                      language === 'en' ? 'Bring all medications and medical documents' : 'Apportez tous les médicaments et documents médicaux',
                    ].map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
                        <CheckCircle sx={{ color: 'success.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{item}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card sx={{ borderTop: '3px solid', borderColor: 'error.main', borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700} color="error.main" mb={2}>
                      ❌ {language === 'en' ? "Don'ts" : 'À Éviter'}
                    </Typography>
                    {[
                      language === 'en' ? "Don't move an injured person unnecessarily" : 'Ne déplacez pas inutilement une personne blessée',
                      language === 'en' ? "Don't give food or water to an unconscious patient" : 'Ne donnez pas à manger ni à boire à un patient inconscient',
                      language === 'en' ? "Don't remove objects embedded in wounds" : 'Ne retirez pas les objets enfoncés dans les blessures',
                      language === 'en' ? "Don't delay calling for help" : 'Ne tardez pas à appeler à l\'aide',
                      language === 'en' ? "Don't panic — stay focused" : 'Ne paniquez pas — restez concentré',
                    ].map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.25 }}>
                        <Warning sx={{ color: 'error.main', fontSize: 18, mt: 0.2, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{item}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default EmergencyPage;
